// Pure helpers behind BP-Talk speech handling.
// Extracted so the tricky logic can be unit-tested in isolation (transcript.test.js).

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// Words that are never a name on their own — used to reject filler/lead-in tokens.
const NAME_STOP = new Set([
  'my', 'name', 'is', 'im', 'i', 'am', 'its', 'it', 'hi', 'hello', 'hey',
  'the', 'a', 'an', 'call', 'me', 'please', 'um', 'uh', 'well', 'so', 'and',
  'you', 'sir', 'thank', 'thanks',
]);

/**
 * Merge a newly-recognised chunk onto already-captured speech without repeating
 * the overlap. Mobile browsers ignore `continuous` and auto-restart on every
 * pause; each restart re-recognises the tail of the previous audio, so naive
 * concatenation produced "my name is" + "my name is Don" = "my name is my name
 * is Don". We drop the largest run of words where the tail of `base` equals the
 * head of `addition` (case-insensitively) before joining.
 */
export function appendDeduped(base, addition) {
  const a = (base || '').trim();
  const b = (addition || '').trim();
  if (!a) return b;
  if (!b) return a;

  const aw = a.split(/\s+/);
  const bw = b.split(/\s+/);
  const max = Math.min(aw.length, bw.length);

  let overlap = 0;
  for (let k = max; k > 0; k--) {
    const tail = aw.slice(aw.length - k).join(' ').toLowerCase();
    const head = bw.slice(0, k).join(' ').toLowerCase();
    if (tail === head) {
      overlap = k;
      break;
    }
  }
  return [...aw, ...bw.slice(overlap)].join(' ');
}

/**
 * Pull the student's name out of a spoken Introductions reply.
 * Returns { name: string } when confident, or { name: null } when the input is
 * unintelligible (e.g. a garbled "My my my name my name is.") — the caller then
 * re-prompts instead of inventing a name like "Is".
 */
export function extractName(transcript) {
  const text = (transcript || '').toLowerCase();

  // Stage 1: explicit lead-in followed by a real (2+ letter, non-filler) word.
  const match = text.match(/(?:my name is|name is|i'?m|i am|call me|it'?s)\s+([a-z]{2,})/);
  if (match && !NAME_STOP.has(match[1])) {
    return { name: capitalize(match[1]) };
  }

  // Stage 2: no clean lead-in — strip filler/stop words and take the last real
  // word left. A name candidate must be at least 2 letters.
  const words = text
    .replace(/['’]/g, '')
    .replace(/[^a-z\s]/g, ' ')
    .split(/\s+/)
    .filter(Boolean)
    .filter((w) => w.length >= 2 && !NAME_STOP.has(w));

  if (words.length) {
    return { name: capitalize(words[words.length - 1]) };
  }

  // Nothing plausible — signal the caller to ask again rather than guess.
  return { name: null };
}

/**
 * Decide the final name after the "spell that for me" step.
 *
 * The spelling step exists to CORRECT a mishearing — but on mobile the spelling
 * itself often comes through garbled (e.g. "J-O-H-N" heard as "J-J-O"), which
 * used to overwrite a name we'd already captured correctly. Rule: if every
 * distinct letter the spelling contains is already in the name we have, treat it
 * as a degraded re-spelling and KEEP the fuller name. Otherwise the student is
 * genuinely correcting us, so adopt the spelling.
 *
 * @param {string} existingName - name captured before the spelling step (may be empty)
 * @param {string} spelledCandidate - cleaned spelling attempt (e.g. "jjo", "don")
 * @returns {string|null}
 */
export function reconcileSpelledName(existingName, spelledCandidate) {
  const s = (spelledCandidate || '').trim();
  const e = (existingName || '').trim();
  if (!s) return e ? capitalize(e) : null;
  if (!e) return capitalize(s);

  const el = e.toLowerCase();
  const sl = s.toLowerCase();
  if (el === sl) return capitalize(e);

  // Every letter of the spelling already in the name we have, and the name is at
  // least as long → it's a partial/garbled spelling of the same name. Keep ours.
  const eLetters = new Set(el.split(''));
  const spellingIsSubset = sl.split('').every((ch) => eLetters.has(ch));
  if (spellingIsSubset && el.length >= sl.length) {
    return capitalize(e);
  }

  // Genuinely different — the student is correcting us. Adopt the spelling.
  return capitalize(s);
}
